import axios from 'axios';
import Helper from './helper.service';
import { saveAs } from 'file-saver';

export const API_URL_PUBLIC = process.env.REACT_APP_API_URL + "api/public/";
export const API_URL_ADMIN = process.env.REACT_APP_API_URL + "api/admin/";

export const IMG_URL = process.env.REACT_APP_IMG_URL + "uploads/artbanne/";

const api = axios.create();

api.interceptors.request.use(config => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
        config.headers['x-access-token'] = token;
    }

    return config;
}, error => {
    return Promise.reject(error);
});

// API functions
export const getSubmissions = async () => {
    try {
        const response = await api.get(`${API_URL_ADMIN}submission`);
        return response.data;

    } catch (error) {
        Helper.toast("Un problème est survenu lors de la récupération des données", "error");
        console.error('Error fetching data:', error);
        throw error;
    }
};

export const getSubmissionByID = async (id) => {
    try {
        const response = await api.get(`${API_URL_ADMIN}submission/${id}`);
        return response.data;

    } catch (error) {
        Helper.toast("Un problème est survenu lors de la récupération des données", "error");
        console.error('Error fetching data:', error);
        throw error;
    }
};

export const updateSubmissionStatus = async (id, status, comment, admin) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}submission/${id}/status`, { status, comment, admin });
        Helper.handleApiResponse(response, "Statut modifié", "Un problème est survenu lors de la modification du statut");
        return response.data;

    } catch (error) {
        console.error('Error updating status:', error);
        Helper.toast("Un problème est survenu lors de la modification du statut", "error");
        throw error;
    }
};

export const updateSubmissionComment = async (id, comment, admin) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}submission/${id}/comment`, { comment, admin });
        Helper.handleApiResponse(response, "Commentaire sauvegardé", "Un problème est survenu lors de la sauvegarde du commentaire");
        return response.data;

    } catch (error) {
        console.error('Error updating status:', error);
        Helper.toast("Un problème est survenu lors de la sauvegarde du commentaire", "error");
        throw error;
    }
};

export const getTransactions = async (id) => {
    try {
        const response = await api.get(`${API_URL_ADMIN}transaction/`);
        return response.data;
    } catch (error) {
        console.error('Error updating status:', error);
        Helper.toast("Un problème est survenu lors du chargement de la facture", "error");
        throw error;
    }
};

export const getTransactionByID = async (id) => {
    try {
        const response = await api.get(`${API_URL_ADMIN}transaction/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error updating status:', error);
        Helper.toast("Un problème est survenu lors du chargement de la facture", "error");
        throw error;
    }
};

export const getTransactionPdf = async (id) => {
    try {
        const response = await api.get(`${API_URL_ADMIN}transaction/${id}/pdf`, {
            responseType: 'blob',
        });

        // Trigger file download manually
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Facture_${id}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Erreur lors du téléchargement de la facture :', error);
        Helper.toast("Un problème est survenu lors du téléchargement de la facture", "error");
        throw error;
    }
};

export const refundTransaction = async (id, payload = {}) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}transaction/${id}/refund`, payload);
        return response.data;

    } catch (error) {
        console.error('Error requesting refund:', error);
        Helper.toast("Un problème est survenu lors du remboursement", "error");
        throw error;
    }
};

const getFileName = (filePath) => {
    return filePath.split('\\').pop().split('/').pop();
};

export const getFile = (filePath) => {
    const token = localStorage.getItem('jwtToken');
    return `${API_URL_ADMIN}submission/files/${getFileName(filePath)}?token=${token}`;
};

export const uploadFile = async (id, formData) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}submission/${id}/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        Helper.handleApiResponse(response, "Fichier ajouté", "Un problème est survenu lors de l'ajout du fichier");
        return response.data;

    } catch (error) {
        console.error('Error deleting file:', error);
        Helper.toast("Un problème est survenu lors de l'ajout du fichier", "error");
        throw error;
    }
};

export const deleteFile = async (id, filePath, admin) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}submission/${id}/delete-file`, { filePath, admin });
        Helper.handleApiResponse(response, "Fichier supprimé", "Un problème est survenu lors de la suppression du fichier");
        return response.data;

    } catch (error) {
        console.error('Error deleting file:', error);
        Helper.toast("Un problème est survenu lors de la suppression du fichier", "error");
        throw error;
    }
};

export const downloadExcel = async () => {
    try {
        const response = await api.get(`${API_URL_ADMIN}submission/export-excel`, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'submissions.xlsx');
    } catch (error) {
        console.error('Error downloading the Excel file:', error);
    }
};

export const downloadUsersExcel = async () => {
    try {
        const response = await api.get(`${API_URL_ADMIN}submission/export-users-excel`, {
            responseType: 'blob'
        });

        const blob = new Blob(
            [response.data],
            { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
        );

        saveAs(blob, 'users-clubby.xlsx');
    } catch (error) {
        console.error('Error downloading users Excel file:', error);
        Helper.toast("Impossible d'exporter les utilisateurs", "error");
        throw error;
    }
};


export const getAllSubdomains = async () => {
    try {
        const response = await api.get(API_URL_ADMIN + 'subdomains/getAllSubdomains');
        console.log(response.data.data);

        return response.data.data;

    } catch (error) {
        Helper.toast("Erreur lors du chargement des sous-domaines", 'error');
        return;
    }
};

export const getSubdomain = async (subdomain) => {
    try {
        const response = await api.get(API_URL_ADMIN + `subdomains/getSubdomain/${subdomain}`);
        return response.data.data;

    } catch (error) {
        Helper.toast("Erreur lors de la récupération du sous-domaine", 'error');
        return;
    }
};

export const saveSubdomain = async (data) => {
    try {
        const response = await api.post(API_URL_ADMIN + 'subdomains/saveSubdomain', data);
        Helper.handleApiResponse(
            response,
            "Sous-domaine enregistré avec succès",
            "Erreur lors de l'enregistrement du sous-domaine"
        );
        return response.data.data;
    } catch (error) {
        const resp = error?.response;

        // build a proper Error instance
        const err = new Error(
            resp?.data?.message || "Erreur lors de l'enregistrement du sous-domaine"
        );
        err.status = resp?.status;
        err.code = resp?.data?.code;

        throw err; // ✅ throw Error object
    }
};

export const deleteSubdomain = async (subdomain, admin) => {
    try {
        const response = await api.post(API_URL_ADMIN + `subdomains/deleteSubdomain/${subdomain}`, { admin });
        Helper.handleApiResponse(response, "Sous-domaine supprimé avec succès", "Erreur lors de la suppression du sous-domaine");

        return response.data.data;
    } catch (error) {
        Helper.toast("Erreur lors de la suppression du sous-domaine", 'error');
        return;
    }
};

export const getService = async (service) => {
    try {
        const response = await api.get(`${API_URL_ADMIN}services/${service}`);
        return response.data.data;

    } catch (error) {
        console.error('Erreur lors du chargement des services :', error);
        Helper.toast("Impossible de charger les services", "error");
        throw error;
    }
};

export const getAllServices = async () => {
    try {
        const response = await api.get(`${API_URL_ADMIN}services`);
        return response.data.data;

    } catch (error) {
        console.error('Erreur lors du chargement des services :', error);
        Helper.toast("Impossible de charger les services", "error");
        throw error;
    }
};

export const saveService = async (serviceData) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}services`, serviceData);
        Helper.handleApiResponse(response, "Service enregistré", "Erreur lors de l'enregistrement du service");
        return response.data;

    } catch (error) {
        console.error('Erreur lors de l\'enregistrement du service :', error);
        Helper.toast("Impossible d'enregistrer le service", "error");
        throw error;
    }
};

export const deleteService = async (serviceId) => {
    try {
        const response = await api.delete(`${API_URL_ADMIN}services/${serviceId}`);
        Helper.handleApiResponse(response, "Service supprimé", "Erreur lors de la suppression du service");
        return response.data;

    } catch (error) {
        console.error('Erreur lors de la suppression du service :', error);
        Helper.toast("Impossible de supprimer le service", "error");
        throw error;
    }
};

export const toggleServiceVisibility = async (serviceId, enable) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}services/toggle`, {
            serviceId,
            enable,
        });
        Helper.handleApiResponse(response, "Visibilité modifiée", "Erreur lors de la modification");
        return response.data;

    } catch (error) {
        console.error('Erreur lors du changement de visibilité :', error);
        Helper.toast("Impossible de modifier la visibilité", "error");
        throw error;
    }
};

export const getAllDocuments = async () => {
    try {
        const response = await api.get(`${API_URL_ADMIN}documents`);
        return response.data.data;

    } catch (error) {
        console.error('Erreur lors du chargement des documents :', error);
        Helper.toast("Impossible de charger les documents", "error");
        throw error;
    }
};

export const getDocumentById = async (id) => {
    try {
        const response = await api.get(`${API_URL_ADMIN}documents/${id}`);
        return response.data.data;

    } catch (error) {
        console.error("Erreur lors du chargement du document :", error);
        Helper.toast("Impossible de charger le document", "error");
        throw error;
    }
};

export const saveDocument = async (doc) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}documents`, doc);
        Helper.handleApiResponse(response, "Document enregistré", "Erreur lors de l'enregistrement du document");
        return response.data.data;

    } catch (error) {
        console.error("Erreur lors de l'enregistrement du document :", error);
        Helper.toast("Impossible d'enregistrer le document", "error");
        throw error;
    }
};

export const deleteDocument = async (id) => {
    try {
        const response = await api.delete(`${API_URL_ADMIN}documents/${id}`);
        Helper.handleApiResponse(response, "Document supprimé", "Erreur lors de la suppression du document");
        return response.data;

    } catch (error) {
        console.error("Erreur lors de la suppression du document :", error);
        Helper.toast("Impossible de supprimer le document", "error");
        throw error;
    }
};

export const getAllBanks = async () => {
    try {
        const response = await api.get(`${API_URL_ADMIN}banks`);
        return response.data.data;
    } catch (error) {
        console.error('Erreur lors du chargement des banques :', error);
        Helper.toast("Impossible de charger les banques", "error");
        throw error;
    }
};

export const setDefaultBank = async (bankId) => {
    try {
        const response = await api.patch(`${API_URL_ADMIN}banks/${bankId}/set-default`);
        Helper.toast("Banque définie par défaut", "success");
        return response.data;

    } catch (error) {
        console.error('Erreur lors de la mise à jour de la banque par défaut :', error);
        Helper.toast("Impossible de définir la banque par défaut", "error");
        throw error;
    }
};

export const getBankById = async (id) => {
    try {
        const response = await api.get(`${API_URL_ADMIN}banks/${id}`);
        return response.data.data;
    } catch (error) {
        console.error("Erreur lors du chargement de la banque :", error);
        Helper.toast("Impossible de charger la banque", "error");
        throw error;
    }
};

export const saveBank = async (bank) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}banks`, bank);
        Helper.handleApiResponse(response, "Banque enregistrée", "Erreur lors de l'enregistrement de la banque");
        return response.data.data;
    } catch (error) {
        console.error("Erreur lors de l'enregistrement de la banque :", error);
        Helper.toast("Impossible d'enregistrer la banque", "error");
        throw error;
    }
};

export const deleteBank = async (id) => {
    try {
        const response = await api.delete(`${API_URL_ADMIN}banks/${id}`);
        Helper.handleApiResponse(response, "Banque supprimée", "Erreur lors de la suppression de la banque");
        return response.data;
    } catch (error) {
        console.error("Erreur lors de la suppression de la banque :", error);
        Helper.toast("Impossible de supprimer la banque", "error");
        throw error;
    }
};

export const getFiskagroupTransactions = async (filters = {}) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}fiskagroup/transactions`, filters);
        return response.data;

    } catch (error) {
        console.error('Erreur lors du chargement des transactions :', error);
        Helper.toast("Impossible de charger les transactions", "error");
        throw error;
    }
};

export const getPaymentLinks = async () => {
    try {
        const response = await api.get(`${API_URL_ADMIN}payment-links`);
        return response.data.data;
    } catch (error) {
        console.error("Erreur lors de la récupération des liens de paiement :", error);
        Helper.toast("Impossible de charger les liens de paiement", "error");
        throw error;
    }
};

export const savePaymentLink = async (paymentLinkData, textSuccess) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}payment-links`, paymentLinkData);
        Helper.handleApiResponse(response, textSuccess, "Erreur lors de l'enregistrement du lien");
        return response.data.data;

    } catch (error) {
        console.error("Erreur lors de l'enregistrement du lien :", error);
        Helper.toast("Impossible d'enregistrer le lien", "error");
        throw error;
    }
};

export const deletePaymentLink = async (id) => {
    try {
        const response = await api.delete(`${API_URL_ADMIN}payment-links/${id}`);
        Helper.handleApiResponse(response, "Lien supprimé", "Erreur lors de la suppression");
        return response.data;
    } catch (error) {
        console.error("Erreur lors de la suppression du lien :", error);
        Helper.toast("Impossible de supprimer le lien", "error");
        throw error;
    }
};

// ───────────────────────────────────────────────────────────────────────────────
// Societies
// ───────────────────────────────────────────────────────────────────────────────

export const getAllSocieties = async () => {
    try {
        const response = await api.get(`${API_URL_ADMIN}societies`);
        return response.data.data;

    } catch (error) {
        console.error('Erreur lors du chargement des sociétés :', error);
        Helper.toast("Impossible de charger les sociétés", "error");
        throw error;
    }
};

export const getSociety = async (id) => {
    try {
        const response = await api.get(`${API_URL_ADMIN}societies/${id}`);
        return response.data.data;

    } catch (error) {
        console.error("Erreur lors du chargement de la société :", error);
        Helper.toast("Impossible de charger la société", "error");
        throw error;
    }
};

export const saveSociety = async (society) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}societies`, society);
        Helper.handleApiResponse(response, "Société enregistrée", "Erreur lors de l'enregistrement de la société");
        return response.data.data;

    } catch (error) {
        console.error("Erreur lors de l'enregistrement de la société :", error);
        Helper.toast("Impossible d'enregistrer la société", "error");
        throw error;
    }
};

export const deleteSociety = async (idOrSociety) => {
    try {
        const id = typeof idOrSociety === 'string' ? idOrSociety : idOrSociety?._id;
        const response = await api.delete(`${API_URL_ADMIN}societies/${id}`);
        Helper.handleApiResponse(response, "Société supprimée", "Erreur lors de la suppression de la société");
        return response.data;

    } catch (error) {
        console.error("Erreur lors de la suppression de la société :", error);
        Helper.toast("Impossible de supprimer la société", "error");
        throw error;
    }
};

export const setDefaultSociety = async (societyId) => {
    try {
        const response = await api.patch(`${API_URL_ADMIN}societies/${societyId}/set-default`);
        Helper.toast("Société définie par défaut", "success");
        return response.data.data;
    } catch (error) {
        console.error('Erreur lors de la mise à jour de la société par défaut :', error);
        Helper.toast("Impossible de définir la société par défaut", "error");
        throw error;
    }
};

// FIELD DEFINITIONS API (same pattern as deleteSociety)
export const getFieldDefinitions = async (params) => {
    try {
        const response = await api.get(`${API_URL_ADMIN}fields`, { params });
        return response.data;
    } catch (error) {
        console.error("Erreur lors du chargement des champs :", error);
        Helper.toast("Impossible de charger les champs", "error");
        throw error;
    }
};

export const createFieldDefinition = async (payload) => {
    try {
        const response = await api.post(`${API_URL_ADMIN}fields`, payload);
        Helper.handleApiResponse(response, "Champ créé", "Erreur lors de la création du champ");
        return response.data;
    } catch (error) {
        console.error("Erreur lors de la création du champ :", error);
        Helper.toast("Impossible de créer le champ", "error");
        throw error;
    }
};

export const updateFieldDefinition = async (idOrField, payload) => {
    try {
        const id = typeof idOrField === "string" ? idOrField : idOrField?._id;
        const body = payload ?? (typeof idOrField === "object" ? idOrField : undefined);
        const response = await api.put(`${API_URL_ADMIN}fields/${id}`, body);
        console.log(response);

        Helper.handleApiResponse(response, "Champ mis à jour", "Erreur lors de la mise à jour du champ");
        return response.data;

    } catch (error) {
        console.error("Erreur lors de la mise à jour du champ :", error);
        Helper.toast("Impossible de mettre à jour le champ", "error");
        throw error;
    }
};

export const deleteFieldDefinition = async (idOrField) => {
    try {
        const id = typeof idOrField === "string" ? idOrField : idOrField?._id;
        const response = await api.delete(`${API_URL_ADMIN}fields/${id}`);
        console.log(response);

        Helper.handleApiResponse(response, "Champ supprimé", "Erreur lors de la suppression du champ");
        return response.data;
    } catch (error) {
        console.error("Erreur lors de la suppression du champ :", error);
        Helper.toast("Impossible de supprimer le champ", "error");
        throw error;
    }
};

export const getAccountingPreview = async ({ societyId, start, end, includeRefunds }) => {
    const params = { societyId, start, end, includeRefunds: !!includeRefunds };
    const res = await api.get(`${API_URL_ADMIN}accounting-export/preview`, { params });
    return res?.data?.data || { count: 0, total: 0, currency: 'EUR' };
};

/** Téléchargement Excel */
export const downloadAccountingXlsx = async ({ societyId, start, end, includeRefunds }) => {
    const params = { societyId, start, end, includeRefunds: !!includeRefunds };
    const res = await api.get(`${API_URL_ADMIN}accounting-export/xlsx`, {
        params,
        responseType: 'blob'
    });
    return new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
};

/** Téléchargement ZIP des PDF */
export const downloadAccountingZip = async ({ societyId, start, end, includeRefunds }) => {
    const params = { societyId, start, end, includeRefunds: !!includeRefunds };
    const res = await api.get(`${API_URL_ADMIN}accounting-export/pdf-bundle`, {
        params,
        responseType: 'blob'
    });
    return new Blob([res.data], { type: 'application/zip' });
};

// Keep your existing imports/instances (api, API_URL_ADMIN, Helper, etc.)

// EMAIL TEMPLATES
export const getAllEmailTemplates = async () => {
    try {
        const res = await api.get(`${API_URL_ADMIN}email-templates`);
        return res.data.data;
    } catch (err) {
        console.error('Erreur lors du chargement des modèles email :', err);
        Helper.toast("Impossible de charger les modèles d'email", "error");
        throw err;
    }
};

export const setDefaultEmailTemplate = async (id) => {
    try {
        const res = await api.post(`${API_URL_ADMIN}email-templates/${id}/default`);
        Helper.handleApiResponse(res, "Modèle d'email par défaut sauvegardé", "Erreur lors de l'enregistrement du modèle par défaut");
        return res.data.data;
    } catch (err) {
        console.error("Erreur lors de l'enregistrement du modèle d'email :", err);
        Helper.toast("Impossible d'enregistrer le modèle d'email", "error");
        throw err;
    }
};

export const getEmailTemplate = async (id) => {
    try {
        const res = await api.get(`${API_URL_ADMIN}email-templates/${id}`);
        return res.data.data;
    } catch (err) {
        console.error("Erreur lors du chargement du modèle d'email :", err);
        Helper.toast("Impossible de charger le modèle d'email", "error");
        throw err;
    }
};

export const saveEmailTemplate = async (payload) => {
    try {
        const res = await api.post(`${API_URL_ADMIN}email-templates`, payload);
        Helper.handleApiResponse(res, "Modèle d'email enregistré", "Erreur lors de l'enregistrement du modèle d'email");
        return res.data.data;
    } catch (err) {
        console.error("Erreur lors de l'enregistrement du modèle d'email :", err);
        Helper.toast("Impossible d'enregistrer le modèle d'email", "error");
        throw err;
    }
};

export const deleteEmailTemplate = async (id) => {
    try {
        const res = await api.delete(`${API_URL_ADMIN}email-templates/${id}`);
        Helper.handleApiResponse(res, "Modèle d'email supprimé", "Erreur lors de la suppression du modèle d'email");
        return res.data;
    } catch (err) {
        console.error("Erreur lors de la suppression du modèle d'email :", err);
        Helper.toast("Impossible de supprimer le modèle d'email", "error");
        throw err;
    }
};

export const previewEmailTemplate = async (payload) => {
    try {
        const res = await api.post(`${API_URL_ADMIN}email-templates/preview`, payload);
        return res.data.data;
    } catch (err) {
        console.error("Erreur lors de la suppression du modèle d'email :", err);
        Helper.toast("Impossible de supprimer le modèle d'email", "error");
        throw err;
    }
};